import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { storageManager } from '../services/auth';
import BrutalistButton from '../components/BrutalistButton';

const SessionTest = () => {
  const { isAuthenticated, student, login, logout } = useAuth();
  const [cookieInfo, setCookieInfo] = useState('');
  const [localStorageInfo, setLocalStorageInfo] = useState('');
  const [refreshCount, setRefreshCount] = useState(0);

  // Track refresh count only on initial mount
  useEffect(() => {
    // Get refresh count from sessionStorage
    const count = sessionStorage.getItem('refreshCount') || '0';
    const newCount = parseInt(count) + 1;
    sessionStorage.setItem('refreshCount', newCount.toString());
    setRefreshCount(newCount);
    
    console.log('SessionTest: Page refresh count updated to', newCount);
  }, []); // Empty dependency array means this runs only once on mount
  
  // Check session info whenever auth state changes
  useEffect(() => {
    const checkSession = async () => {
      console.log('SessionTest: Checking session info');
      
      // Get cookie info
      const cookies = document.cookie.split(';').map(c => c.trim());
      const tokenCookie = cookies.find(c => c.startsWith('auth_token='));
      
      // Get localStorage info
      const tokenData = localStorage.getItem('auth_token_data');
      const version = localStorage.getItem('auth_version');
      
      // Update state
      setCookieInfo(tokenCookie || 'No auth_token cookie found');
      setLocalStorageInfo(
        `auth_token_data: ${tokenData ? 'exists' : 'not found'}, ` +
        `auth_version: ${version || 'not found'}`
      );
      
      // Log detailed info
      console.log('SessionTest: Auth state', { isAuthenticated, student });
      console.log('SessionTest: Cookies', cookies);
      console.log('SessionTest: localStorage', {
        tokenData: tokenData ? JSON.parse(tokenData) : null,
        version
      });
      
      // Check stored token
      const storedToken = await storageManager.getStoredToken();
      console.log('SessionTest: StorageManager.getStoredToken() result', storedToken);
    };
    
    checkSession();
  }, [isAuthenticated, student]);
  
  // Test login with a sample token
  const handleTestLogin = async () => {
    try {
      // This is just for testing - in a real app, you'd get this from user input
      const testToken = 'ACC-12345678-1234';
      
      console.log('SessionTest: Testing login with token', testToken);
      
      // Call the login function from AuthContext
      await login({ token: testToken });
      
      console.log('SessionTest: Login successful');
    } catch (error) {
      console.error('SessionTest: Login failed', error);
      alert(`Login failed: ${error.message}`);
    }
  };
  
  // Test logout
  const handleTestLogout = async () => {
    try {
      console.log('SessionTest: Testing logout');
      
      // Call the logout function from AuthContext
      await logout();
      
      console.log('SessionTest: Logout successful');
    } catch (error) {
      console.error('SessionTest: Logout failed', error);
      alert(`Logout failed: ${error.message}`);
    }
  };
  
  // Force page refresh
  const handleRefresh = () => {
    console.log('SessionTest: Forcing page refresh');
    window.location.reload();
  };
  
  // Clear all storage
  const handleClearStorage = () => {
    console.log('SessionTest: Clearing all storage');
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear cookies
    document.cookie.split(';').forEach(c => {
      document.cookie = c.replace(/^ +/, '').replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
    });
    
    alert('All storage cleared. Refreshing page...');
    window.location.reload();
  };
  
  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>Session Persistence Test</h1>
      
      <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
        <h2>Current Session State</h2>
        <p><strong>Page Refresh Count:</strong> {refreshCount}</p>
        <p><strong>Authentication Status:</strong> {isAuthenticated ? 'Authenticated' : 'Not Authenticated'}</p>
        <p><strong>Student Info:</strong> {student ? JSON.stringify(student) : 'No student info'}</p>
        <p><strong>Cookie Info:</strong> {cookieInfo}</p>
        <p><strong>LocalStorage Info:</strong> {localStorageInfo}</p>
      </div>
      
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <BrutalistButton onClick={handleTestLogin} disabled={isAuthenticated}>
          Test Login
        </BrutalistButton>
        
        <BrutalistButton onClick={handleTestLogout} disabled={!isAuthenticated}>
          Test Logout
        </BrutalistButton>
        
        <BrutalistButton onClick={handleRefresh}>
          Refresh Page
        </BrutalistButton>
        
        <BrutalistButton onClick={handleClearStorage}>
          Clear All Storage
        </BrutalistButton>
      </div>
      
      <div style={{ marginTop: '30px' }}>
        <h2>Debugging Instructions</h2>
        <ol>
          <li>Click "Test Login" to simulate a login (this will attempt to validate a test token)</li>
          <li>Once logged in, check the session state information above</li>
          <li>Click "Refresh Page" to test if the session persists after a refresh</li>
          <li>Check the browser console for detailed logs</li>
          <li>Use "Clear All Storage" to reset everything if needed</li>
        </ol>
        <p><strong>Note:</strong> The test login may fail if the API endpoint is not available or rejects the test token.</p>
      </div>
    </div>
  );
};

export default SessionTest;